import type { PivotLayout, PivotLayoutFilter } from '@ztechapps/common.contract'
import { isObject } from '../is-object'
import { isPivotCalculatedMeasure } from '../is-pivot-calculated-measure'

/**
 * The checks every filter that ranks or compares a Rows or Columns field by an aggregated value shares: the entry is
 * an object, the field sits in Rows or Columns, and its measure is an aggregated value of the layout. `entry` is the
 * filter's `valueFilter` or `valueComparison`, `subject` names it in a problem (`a value filter`). Returns the first
 * problem, or the field's position in its axis — Rows first — when there is none. Reads untrusted model-written
 * layouts, so every level is checked before it is read.
 */
export const findPivotRankedFieldProblem = ({
  layout,
  filter,
  entry,
  subject,
}: {
  layout: Pick<PivotLayout, 'rows' | 'columns' | 'measures'>
  filter: PivotLayoutFilter
  entry: unknown
  subject: string
}): { problem?: string; position: number } => {
  const name = String(filter.field)
  if (!isObject(entry)) return { problem: `${name} has ${subject} that is not an object.`, position: -1 }
  const isSameItem = (item: unknown): boolean =>
    isObject(item) && item.field === filter.field && item.dateGroup === filter.dateGroup
  const rowIndex = layout.rows.findIndex(isSameItem)
  const columnIndex = layout.columns.findIndex(isSameItem)
  if (rowIndex === -1 && columnIndex === -1)
    return {
      problem: `${name} has ${subject} but is not in Rows or Columns — only a Rows or Columns field takes one.`,
      position: -1,
    }
  const measure = isObject(entry.measure) ? entry.measure : {}
  const isAggregatedMeasure = layout.measures.some(
    (item) =>
      isObject(item) &&
      !isPivotCalculatedMeasure(item) &&
      item.field === measure.field &&
      item.aggregation === measure.aggregation
  )
  if (!isAggregatedMeasure)
    return {
      problem: `${name} uses ${String(measure.field)} (${String(measure.aggregation)}), which is not an aggregated value of this layout.`,
      position: -1,
    }
  return { position: rowIndex === -1 ? columnIndex : rowIndex }
}
